/**
 * 案件なしの{schedule}（#291、docs/102 §3.1）
 *   CreateScheduleDialog … 全体の{schedule}の題の段の「{schedule}作成」→ 題（必須）・{customer}（任意。取引先の引き出し）・担当（既定 自分）→「作成」→ /schedules/:id
 *   AttachDialog         … 案件なしの{schedule}の ⋮「案件に紐づける」→ {schedule}の無い{project}を探して「紐づける」／下に「新しい{project}を作成」
 *                          （{project}作成の画面を題・{customer}を初期値にして ?scheduleId= で開く。保存したら紐づけて{schedule}へ戻る）
 *   紐づけを外す機能は作らない（★1）
 */
import { SCHEDULE_LIMITS, type ScheduleAssignees, type ScheduleAttachCandidate, type ScheduleView } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { api, messageOf } from "../../api.js";
import { useMe } from "../../auth.jsx";
import { CrIcon } from "../../components/Create.jsx";
import { PartnerChip, PartnerField, type PickedPartner } from "../../components/PartnerPickerDrawer.jsx";
import { ErrorText, Field, guardComposingEnter } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { Dlg } from "./Dialogs.jsx";
import { SCHEDULE_MSG } from "./msg.js";

/** {schedule}作成（案件なし）。作ったら /schedules/:id へ */
export function CreateScheduleDialog({ onCancel }: { onCancel: () => void }) {
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const [title, setTitle] = useState("");
  const [partner, setPartner] = useState<PickedPartner | null>(null);
  const myId = me.data?.user.id ?? "";
  const [owner, setOwner] = useState<string>("");
  const cands = useQuery({ queryKey: ["schedule-assignees", "owner"], queryFn: () => api<ScheduleAssignees>("GET", "/schedule/assignees"), staleTime: 60_000 });
  useEffect(() => {
    if (!owner && myId) setOwner(myId);
  }, [myId, owner]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const submit = async () => {
    if (!title.trim()) return setErr(SCHEDULE_MSG.titleRequired);
    setBusy(true);
    setErr(undefined);
    try {
      const v = await api<ScheduleView>("POST", "/schedules", { title: title.trim(), partnerId: partner?.id ?? null, ownerUserId: owner || null });
      void qc.invalidateQueries({ queryKey: ["schedule-overview"] });
      qc.setQueryData(["schedule", "s", v.schedule!.id], v);
      nav(`/schedules/${v.schedule!.id}`);
    } catch (e) {
      setErr(messageOf(e));
      setBusy(false);
    }
  };
  const users = cands.data?.users ?? [];
  return (
    <Dlg
      title={SCHEDULE_MSG.createTitle}
      testId="sch-create-dlg"
      icon={<CrIcon k="cal" />}
      onCancel={onCancel}
      foot={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            取消
          </button>
          <button type="button" className="btn primary" disabled={busy || !title.trim()} onClick={submit} data-testid="sch-create-ok">
            作成
          </button>
        </>
      }
    >
      <Field label={SCHEDULE_MSG.title} id="sch-create-title">
        <input
          id="sch-create-title"
          className="inp"
          value={title}
          maxLength={100}
          autoFocus
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            guardComposingEnter(e);
            if (e.key === "Enter" && !e.defaultPrevented) void submit();
          }}
          data-testid="sch-create-title"
        />
      </Field>
      {partner ? (
        <PartnerChip label={L.customer} name={partner.displayName || partner.name} branch={null} contact={null} onChange={() => setPartner(null)} testId="sch-create-picked" changeTestId="sch-create-partner-change" />
      ) : (
        <PartnerField role="customer" label={L.customer} value={null} onPick={setPartner} testId="sch-create-partner" />
      )}
      <Field label={SCHEDULE_MSG.owner} id="sch-create-owner">
        <select id="sch-create-owner" className="inp" value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="sch-create-owner">
          <option value="">{t("—")}</option>
          {users.some((u) => u.id === myId) || !myId ? null : <option value={myId}>{me.data?.user.name ?? ""}</option>}
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </Field>
      <ErrorText>{err}</ErrorText>
    </Dlg>
  );
}

/** 案件に紐づける（案件なしの{schedule}だけ） */
export function AttachDialog({ scheduleId, title, partnerId, onCancel, onAttached }: { scheduleId: string; title: string; partnerId: string | null; onCancel: () => void; onAttached: (v: ScheduleView) => void }) {
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [q0, setQ0] = useState("");
  useEffect(() => {
    const tm = setTimeout(() => setQ0(text.trim()), 300);
    return () => clearTimeout(tm);
  }, [text]);
  const q = useQuery({
    queryKey: ["schedule-attach-candidates", q0],
    queryFn: () => api<ScheduleAttachCandidate[]>("GET", `/schedule/attach-candidates${q0 ? `?q=${encodeURIComponent(q0)}` : ""}`),
    staleTime: 0,
  });
  const [pick, setPick] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const list = q.data ?? [];
  const id = pick && list.some((x) => x.id === pick) ? pick : null;
  const toNew = () => {
    const p = new URLSearchParams({ scheduleId, name: title.slice(0, SCHEDULE_LIMITS.name) });
    if (partnerId) p.set("client", partnerId);
    nav(`/groups/new?${p.toString()}`);
  };
  return (
    <Dlg
      title={SCHEDULE_MSG.attachTitle}
      testId="sch-attach"
      icon={<CrIcon k="link" />}
      onCancel={onCancel}
      foot={
        <>
          <button type="button" className="btn text" onClick={toNew} data-testid="sch-attach-new">
            {SCHEDULE_MSG.attachNew}
          </button>
          <span className="sp" />
          <button type="button" className="btn" onClick={onCancel}>
            取消
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!id || busy}
            onClick={async () => {
              setBusy(true);
              setErr(undefined);
              try {
                onAttached(await api<ScheduleView>("POST", `/schedules/${scheduleId}/attach`, { groupId: id }));
              } catch (e) {
                setErr(messageOf(e));
                setBusy(false);
              }
            }}
            data-testid="sch-attach-ok"
          >
            {SCHEDULE_MSG.attachButton}
          </button>
        </>
      }
    >
      <Field label={SCHEDULE_MSG.attachSearch} id="sch-attach-q">
        <input id="sch-attach-q" className="inp" value={text} autoFocus onChange={(e) => setText(e.target.value)} onKeyDown={guardComposingEnter} autoComplete="off" data-testid="sch-attach-q" />
      </Field>
      {q.data && !list.length ? <p style={{ margin: 0 }}>{SCHEDULE_MSG.attachEmpty}</p> : null}
      {list.length ? (
        <div className="sch-picklist" role="radiogroup" aria-label={SCHEDULE_MSG.attachTitle}>
          {list.map((x) => (
            <label key={x.id} className="ck" data-testid="sch-attach-cand">
              <input type="radio" name="sch-attach" checked={id === x.id} onChange={() => setPick(x.id)} />
              <span>
                {x.name}
                <small className="muted">　{x.clientName}</small>
              </span>
            </label>
          ))}
        </div>
      ) : null}
      <ErrorText>{err}</ErrorText>
    </Dlg>
  );
}
